import {
  AlignItems,
  AlignSelf,
  Box,
  Color,
  Display,
  FlexDirection,
  FontFamily,
  Heading1,
  Image,
  JustifyContent,
  Space,
  Text,
  TextAlign,
  Visible,
  useTheme,
} from '@gousto-internal/citrus-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { DiscountBoxes, FriendGiftText, CTABlock } from './components/'
import { HERO_CONFIG, IMAGES } from './Hero.config'
import { RafCampaignDetails } from '../../hooks/useFetchRafCampaignDetails'
import { canUseWindow } from '@library/environment'
import { GetStarted } from '../GetStarted/GetStarted'
import { RafFloatingCtaAndDiscountBannerType, RafLandingPageType } from '../../Pages/RafLandingPage'
import { debounce } from 'lodash'
import { queueTrackingEvent } from '@library/tracking-integration'
import { RafTrackingEventType, StickyTrackingEvent } from '../../tracking/trackingConfig'

export const Hero = ({
  firstBoxDiscount,
  secondBoxDiscount,
  giftYourFriendCredit,
  onScrollHandler,
  isFloatingCtaAndDiscountBannerEnabled,
}: RafCampaignDetails &
  RafFloatingCtaAndDiscountBannerType &
  Pick<RafLandingPageType, 'onScrollHandler'>) => {
  const [isSticky, setIsSticky] = useState(false)
  const [maxHeight, setMaxHeight] = useState(0)
  const { theme } = useTheme()
  const breakpointToMedium = Number(theme.tokens.breakpoints[0].replace('px', ''))

  const heroRef = useRef<HTMLDivElement>(null)
  const stickyCTARef = useRef<HTMLDivElement>(null)

  const onScroll = useCallback(() => {
    onScrollHandler({ heroRef, stickyCTARef, setIsSticky, setMaxHeight })
  }, [onScrollHandler])

  const debounceScroll = debounce(onScroll, 200)

  useEffect(() => {
    if (canUseWindow()) {
      window?.addEventListener('scroll', debounceScroll)
    }

    if (window?.innerWidth < breakpointToMedium) {
      queueTrackingEvent<RafTrackingEventType>(
        StickyTrackingEvent({ isVisible: isSticky, name: 'sticky_footer_cta_with_discount' }),
      )
    }

    return () => {
      if (canUseWindow()) {
        window.removeEventListener('scroll', debounceScroll)
      }
    }
  }, [breakpointToMedium, debounceScroll, isSticky])

  return (
    <Box
      paddingH={[3, 8]}
      paddingV={[0, null, 14]}
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      bg={Color.ColdGrey_50}
      justifyContent={JustifyContent.Center}
      alignItems={AlignItems.Center}
      ref={heroRef}
    >
      <Box
        display={Display.Flex}
        alignItems={[AlignItems.Center]}
        justifyContent={JustifyContent.Center}
        flexDirection={[FlexDirection.Column, null, FlexDirection.Row]}
        width="100%"
        maxWidth="1200px"
        className="main"
        gap={[2, 0, 0]}
      >
        <Visible display={[Display.Block, null, Display.None]}>
          <Box maxWidth="360px" justifyContent={JustifyContent.Center}>
            <Image
              fit="contain"
              srcSet={`${IMAGES.mobile.webp.x1} 1x, ${IMAGES.mobile.webp.x2} 2x`}
              src={IMAGES.mobile.png}
              alt=""
            />
          </Box>
        </Visible>
        <Box>
          <Box>
            <Heading1
              fontFamily={FontFamily.Bold}
              color={Color.Secondary_400}
              textAlign={[TextAlign.Center, null, TextAlign.Left]}
            >
              {HERO_CONFIG.heading}
            </Heading1>
            <Box
              paddingV={[1, 3]}
              display={Display.Flex}
              flexDirection={FlexDirection.Column}
              justifyContent={JustifyContent.Center}
            >
              <Text size={[2, 3]} textAlign={[TextAlign.Center, null, TextAlign.Left]}>
                Join over 500,000 customers who{' '}
                <Text display={Display.Inline} fontFamily={FontFamily.Bold}>
                  referred
                </Text>{' '}
                a friend to Gousto.
              </Text>
            </Box>
            <Visible display={[Display.None, null, Display.Block]}>
              <Box paddingV={[0, 3]} paddingBottom={[0, 5]}>
                <FriendGiftText giftYourFriendCredit={giftYourFriendCredit} />
              </Box>
            </Visible>
            <Visible display={[Display.None, null, Display.Block]}>
              <CTABlock />
            </Visible>
          </Box>
        </Box>
        <Space direction="horizontal" size={4} />
        <Box display={Display.Flex} gap={2}>
          <DiscountBoxes
            firstBoxDiscount={firstBoxDiscount}
            secondBoxDiscount={secondBoxDiscount}
          />
          <Visible display={[Display.None, null, Display.Block]}>
            <Box display={Display.Flex} alignSelf={AlignSelf.Start}>
              <Image
                srcSet={`${IMAGES.desktop.webp.x1} x1, ${IMAGES.desktop.webp.x2} x2`}
                src={IMAGES.desktop.png}
                alt=""
                fit="contain"
              />
            </Box>
          </Visible>
        </Box>
      </Box>
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        justifyContent={JustifyContent.Center}
        alignItems={AlignItems.Center}
        width="100%"
      >
        <Visible display={[Display.Block, null, Display.None]}>
          <Space direction="vertical" size={4} />
          <FriendGiftText giftYourFriendCredit={giftYourFriendCredit} />
          <Space direction="vertical" size={4} />
          <CTABlock />
          <Space direction="vertical" size={6} />
        </Visible>
        {isFloatingCtaAndDiscountBannerEnabled && (
          <GetStarted
            getStartedText={`Get started with ${firstBoxDiscount || '--'}% off`}
            ctaRef={stickyCTARef}
            isHeroCTA={false}
            isSticky={isSticky}
            maxHeight={maxHeight}
          />
        )}
      </Box>
    </Box>
  )
}
